import { z } from 'zod';
import { RouteContract, ServiceContract, InferResponse, CONTRACT_META, ContractMeta, getContractPrefix } from './types';
import { TypedFormData } from './formData';

/**
 * Check if a RouteContract field was provided (not undefined).
 * Uses non-distributive check to handle union types correctly.
 */
type IsProvided<T> = [T] extends [undefined] ? false : true;

/**
 * Check if a RouteContract has multipart/form-data content type.
 */
type IsMultipart<T extends RouteContract> =
	T extends RouteContract<any, any, any, any, any, any, any, 'multipart/form-data'> ? true : false;

/**
 * Clean params type - only includes fields defined in the contract, and makes them required.
 * For multipart routes, body must be a TypedFormData branded with the schema fields —
 * the compiler rejects extra or missing keys via key-invariant branding.
 */
type CleanParams<T extends RouteContract> = (IsProvided<T['params']> extends true
	? { params: z.infer<NonNullable<T['params']>> }
	: {}) &
	(IsProvided<T['query']> extends true ? { query: z.infer<NonNullable<T['query']>> } : {}) &
	(IsProvided<T['headers']> extends true ? { headers?: z.infer<NonNullable<T['headers']>> } : {}) &
	(IsProvided<T['body']> extends true
		? IsMultipart<T> extends true
			? { body: TypedFormData<z.infer<NonNullable<T['body']>>> }
			: { body: z.infer<NonNullable<T['body']>> }
		: {}) & {
		requestOptions?: {
			timeout?: number;
			disableAuth?: boolean;
			isGlobal?: boolean;
		};
	};

/**
 * Check if any params are required
 */
type HasRequiredParams<T extends RouteContract> =
	IsProvided<T['params']> extends true
		? true
		: IsProvided<T['query']> extends true
			? true
			: IsProvided<T['body']> extends true
				? true
				: false;

/**
 * Client method signature - params required only if route has params/query/headers/body
 */
export type ClientMethod<T extends RouteContract> =
	HasRequiredParams<T> extends true
		? (params: CleanParams<T>) => Promise<InferResponse<T>>
		: (params?: CleanParams<T>) => Promise<InferResponse<T>>;

/**
 * Generated client type from a service contract
 */
export type ContractClient<T extends ServiceContract> = {
	[K in Extract<keyof T, string>]: ClientMethod<T[K]>;
};

/**
 * Minimal interface matching RequestService's methods
 */
export interface IRequestServiceLike {
	get(
		url: string,
		options?: { headers?: any; params?: any; timeout?: number; disableAuth?: boolean; isGlobal?: boolean }
	): Promise<any>;
	post(
		url: string,
		options?: {
			headers?: any;
			body?: any;
			params?: any;
			timeout?: number;
			disableAuth?: boolean;
			isGlobal?: boolean;
		}
	): Promise<any>;
	put(
		url: string,
		options?: {
			headers?: any;
			body?: any;
			params?: any;
			timeout?: number;
			disableAuth?: boolean;
			isGlobal?: boolean;
		}
	): Promise<any>;
	delete(
		url: string,
		options?: {
			headers?: any;
			body?: any;
			params?: any;
			timeout?: number;
			disableAuth?: boolean;
			isGlobal?: boolean;
		}
	): Promise<any>;
}

export interface CreateClientOptions {
	baseURL: string;
	requestService: IRequestServiceLike;
}

/**
 * Replace path params like :id with actual values
 */
function interpolatePath(path: string, params: Record<string, any> | undefined): string {
	if (!params) return path;

	return path.replace(/:([a-zA-Z_][a-zA-Z0-9_]*)/g, (_, key) => {
		const value = params[key];
		if (value === undefined) {
			throw new Error(`Missing path parameter: ${key}`);
		}
		return encodeURIComponent(String(value));
	});
}

/**
 * Create a type-safe client from a service contract (for frontend RequestService)
 *
 * @example
 * ```typescript
 * const sequencesClient = createClient(sequenceContract, {
 *   baseURL: '/activities',
 *   requestService,
 * });
 *
 * // Fully typed - query is required, params are enforced
 * const sequences = await sequencesClient.getSequences({
 *   query: { projectId: 'abc' },
 * });
 * ```
 */
export function createClient<T extends ServiceContract>(contract: T, options: CreateClientOptions): ContractClient<T> {
	const { baseURL, requestService } = options;

	const client = {} as ContractClient<T>;

	for (const [name, route] of Object.entries(contract) as [keyof T, RouteContract][]) {
		(client as any)[name] = async (params: any = {}) => {
			const path = interpolatePath(route.path, params.params);
			const url = `${baseURL}${path}`;
			const headers = params.headers;
			const body = params.body;
			const requestOptions = params.requestOptions;

			let data: any;

			switch (route.method) {
				case 'GET':
					data = await requestService.get(url, {
						params: params.query,
						headers,
						timeout: requestOptions?.timeout,
						disableAuth: requestOptions?.disableAuth,
						isGlobal: requestOptions?.isGlobal,
					});
					break;
				case 'POST':
					data = await requestService.post(url, {
						params: params.query,
						body,
						headers,
						timeout: requestOptions?.timeout,
						disableAuth: requestOptions?.disableAuth,
						isGlobal: requestOptions?.isGlobal,
					});
					break;
				case 'PUT':
					data = await requestService.put(url, {
						params: params.query,
						body,
						headers,
						timeout: requestOptions?.timeout,
						disableAuth: requestOptions?.disableAuth,
						isGlobal: requestOptions?.isGlobal,
					});
					break;
				case 'DELETE':
					data = await requestService.delete(url, {
						params: params.query,
						body,
						headers,
						timeout: requestOptions?.timeout,
						disableAuth: requestOptions?.disableAuth,
						isGlobal: requestOptions?.isGlobal,
					});
					break;
				default:
					throw new Error(`Unsupported method: ${route.method}`);
			}

			return data;
		};
	}

	return client;
}

/**
 * Service client type — auto-extracts named contracts from a module's exports.
 * Keys are the contract names (from defineContract), values are ContractClient instances.
 */
export type ServiceClient<M> = {
	[K in keyof M as M[K] extends { readonly [CONTRACT_META]: ContractMeta<infer N> }
		? N extends ''
			? never
			: N
		: never]: M[K] extends ServiceContract ? ContractClient<M[K]> : never;
};

export interface CreateServiceClientOptions {
	requestService: IRequestServiceLike;
}

/**
 * Create a service-level client from barrel exports. Auto-discovers contracts via CONTRACT_META.
 *
 * @example
 * ```typescript
 * import * as activitiesContracts from '@shared/contracts/activities';
 *
 * const activitiesApi = createServiceClient('/activities', activitiesContracts, { requestService });
 * activitiesApi.sequences.getSequences({ params: { projectId } });
 * activitiesApi.milestones.getMilestoneById({ params: { projectId, milestoneId } });
 * ```
 */
export function createServiceClient<M extends Record<string, unknown>>(
	serviceBaseURL: string,
	moduleExports: M,
	options: CreateServiceClientOptions
): ServiceClient<M> {
	const client: any = {};

	for (const value of Object.values(moduleExports)) {
		if (value && typeof value === 'object' && CONTRACT_META in value) {
			const meta = (value as any)[CONTRACT_META] as ContractMeta;
			client[meta.name] = createClient(value as ServiceContract, {
				baseURL: `${serviceBaseURL}${getContractPrefix(value as ServiceContract)}`,
				requestService: options.requestService,
			});
		}
	}

	return client;
}
